import { useMemo, useState } from "react";

import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { Tabs } from "@/design-system/Tabs";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { ProjectSelectionTable } from "./ProjectSelectionTable";
import type { PortfolioIntegrationConfiguration, PortfolioIntegrationProject } from "./types";

type WorkspaceTab = "bulk" | "projects";

export type PortfolioIntegrationWorkspaceContentProps<TConfiguration extends PortfolioIntegrationConfiguration> = {
  rows: PortfolioIntegrationProject<TConfiguration>[];
  selectedProjectIds: Set<string>;
  onContinueToProjectSetup: () => void;
};

type PortfolioIntegrationWorkspaceProps<TConfiguration extends PortfolioIntegrationConfiguration> = {
  name: string;
  description: string;
  rows: PortfolioIntegrationProject<TConfiguration>[];
  showIntegrationStatus: boolean;
  renderBulkEditor: (props: PortfolioIntegrationWorkspaceContentProps<TConfiguration>) => React.ReactNode;
  renderProjectSetup: (props: PortfolioIntegrationWorkspaceContentProps<TConfiguration>) => React.ReactNode;
};

/** Provides the shared project selection and edit flow around one integration-specific page. */
export function PortfolioIntegrationWorkspace<TConfiguration extends PortfolioIntegrationConfiguration>({
  name,
  description,
  rows,
  showIntegrationStatus,
  renderBulkEditor,
  renderProjectSetup,
}: PortfolioIntegrationWorkspaceProps<TConfiguration>): React.ReactNode {
  const { t } = useTranslation();
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<WorkspaceTab>("bulk");
  const manageableSelectedProjectIds = useMemo(
    () =>
      new Set(
        rows.filter((row) => row.canManage && selectedProjectIds.has(row.project.id)).map((row) => row.project.id),
      ),
    [rows, selectedProjectIds],
  );
  const contentProps = {
    rows,
    selectedProjectIds: manageableSelectedProjectIds,
    onContinueToProjectSetup: () => setActiveTab("projects"),
  };

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={name}
      title={t("page.portfolio-integrations.workspace.title", { integration: name })}
      breadcrumbs={
        <Breadcrumbs
          pages={[{ name: t("page.available-integrations.title"), to: routes.portfolio.integrations() }, { name }]}
        />
      }
    >
      <Stack gap="lg">
        <p className="max-w-measure-wide text-body text-text-secondary">{description}</p>

        <section className="flex flex-col gap-space-sm" aria-labelledby="select-projects-title">
          <h2 id="select-projects-title" className="text-heading-2">
            {t("page.portfolio-integrations.workspace.select-projects-title")}
          </h2>
          <ProjectSelectionTable
            rows={rows}
            selectedProjectIds={manageableSelectedProjectIds}
            onSelectionChange={setSelectedProjectIds}
            showIntegrationStatus={showIntegrationStatus}
          />
        </section>

        {manageableSelectedProjectIds.size === 0 ? (
          <Notice variant="special" message={t("page.portfolio-integrations.workspace.configure-empty")} />
        ) : (
          <section className="flex flex-col gap-space-sm" aria-labelledby="configure-projects-title">
            <h2 id="configure-projects-title" className="text-heading-2">
              {t("page.portfolio-integrations.workspace.configure-projects-title")}
            </h2>
            <Tabs.Root
              activeTab={activeTab}
              tabs={[
                { value: "bulk", label: t("page.portfolio-integrations.tabs.bulk-editable") },
                { value: "projects", label: t("page.portfolio-integrations.tabs.project-specific") },
              ]}
              onSelectTab={setActiveTab}
            >
              <Tabs.Content value="bulk">{renderBulkEditor(contentProps)}</Tabs.Content>
              <Tabs.Content value="projects">{renderProjectSetup(contentProps)}</Tabs.Content>
            </Tabs.Root>
          </section>
        )}
      </Stack>
    </DocumentPage>
  );
}
